'use client';

import { Menu as BaseMenu } from '@base-ui/react/menu';
import { useMemo } from 'react';

import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { MenuItemContext } from './internal/MenuItemContext';
import {
  menuItemBaseStyles,
  menuItemFocusStyles,
} from './internal/menuItemStyles';
import { MenuSelectionContext } from './internal/MenuSelectionContext';
import { useMenuSingleSelectGroupContext } from './internal/MenuSingleSelectGroupContext';
import { useMenuItemInteraction } from './internal/useMenuItemInteraction';

const menuSingleSelectItemClasses = {
  root: 'zds-menu-single-select-item-root',
} as const;

const menuSingleSelectItemStyles = tv({
  extend: menuItemBaseStyles,
  base: [menuSingleSelectItemClasses.root],
});

interface MenuSingleSelectItemBaseProps {
  /**
   * Unique value within the group.
   */
  value: string;
  /**
   * Text used for typeahead matching.
   */
  typeaheadValue?: string;
  /**
   * Prevent the menu from closing when this item is activated.
   *
   * @default false
   */
  disableAutoClose?: boolean;
  /**
   * Disabled but still keyboard-focusable.
   *
   * @default false
   */
  isDisabled?: boolean;
}

type MenuSingleSelectItemProps = ComponentProps<
  MenuSingleSelectItemBaseProps,
  'div'
>;

type MenuSingleSelectItemComponent = Component<
  MenuSingleSelectItemBaseProps,
  'div'
>;

const MenuSingleSelectItem: MenuSingleSelectItemComponent = (props) => {
  const {
    value,
    typeaheadValue,
    disableAutoClose = false,
    isDisabled: isDisabledProp,
    className,
    children,
    ...rest
  } = props;

  const groupContext = useMenuSingleSelectGroupContext();
  const isDisabled = isDisabledProp ?? groupContext?.isDisabled ?? false;
  const isSelected = groupContext?.value === value;

  const selectionContext = useMemo(() => ({ isSelected }), [isSelected]);

  const { interactionProps } = useMenuItemInteraction({ isDisabled });
  const itemContext = useMemo(() => ({ isDisabled }), [isDisabled]);

  return (
    <MenuItemContext.Provider value={itemContext}>
      <MenuSelectionContext.Provider value={selectionContext}>
        <BaseMenu.RadioItem
          {...rest}
          value={value}
          label={typeaheadValue}
          closeOnClick={!disableAutoClose}
          disabled={isDisabled}
          render={(baseProps) => (
            <div
              {...mergeProps(baseProps, interactionProps)}
              className={menuItemFocusStyles.root({
                className: menuSingleSelectItemStyles({
                  isDisabled,
                  className,
                }),
              })}
            />
          )}
        >
          {children}
        </BaseMenu.RadioItem>
      </MenuSelectionContext.Provider>
    </MenuItemContext.Provider>
  );
};

MenuSingleSelectItem.displayName = 'Menu.SingleSelectItem';

export {
  MenuSingleSelectItem,
  type MenuSingleSelectItemProps,
  type MenuSingleSelectItemBaseProps,
  menuSingleSelectItemClasses,
};
